/**
 * E2E Flow Editor
 * Issue #313: Interactive editor for E2E user interaction flow diagrams
 *
 * Extends E2EFlowRenderer with editing capabilities:
 * - Add/remove steps
 * - Edit step details
 * - Connect/disconnect steps
 * - Drag and reorder
 */

import { useState, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  Edge,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

import { e2eFlowNodeTypes, getStepTypeColors, STEP_TYPE_LABELS } from './E2EFlowNodes';
import { convertE2EFlowToReactFlow, type E2EFlowData } from './e2eFlowConverter';
import type { FlowStepType, E2EFlowStep, E2EFlowPath } from '@veodiagram/shared';

// ============= Types =============

interface E2EFlowEditorProps {
  data: E2EFlowData;
  className?: string;
  onSave?: (data: E2EFlowData) => void;
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;
}

export interface E2EFlowEditorHandle {
  fitView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  applyAutoLayout: () => void;
  addStep: (type: FlowStepType, pathId?: string) => void;
  deleteSelected: () => void;
  save: () => void;
  getFlowData: () => E2EFlowData;
}

interface StepEditorModalProps {
  node: Node | null;
  onClose: () => void;
  onSave: (nodeId: string, data: Partial<E2EFlowStep>) => void;
}

// ============= Step Editor Modal =============

function StepEditorModal({ node, onClose, onSave }: StepEditorModalProps) {
  const [name, setName] = useState(node?.data?.label as string || '');
  const [description, setDescription] = useState(node?.data?.description as string || '');
  const [type, setType] = useState<FlowStepType>(node?.data?.type as FlowStepType || 'service-call');

  if (!node) return null;

  const handleSave = () => {
    onSave(node.id, {
      name,
      description,
      type,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 w-96">
        <h3 className="text-lg font-semibold mb-4 text-slate-800 dark:text-slate-200">
          Edit Step
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Step Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as FlowStepType)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              {Object.entries(STEP_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ============= Step Type Palette =============

function StepTypePalette({
  onAddStep,
}: {
  onAddStep: (type: FlowStepType) => void;
}) {
  const stepTypes: FlowStepType[] = [
    'user-action',
    'ui-component',
    'api-call',
    'api-route',
    'service-call',
    'database',
    'external-api',
    'state-change',
    'navigation',
    'error',
    'cache',
  ];

  return (
    <div className="bg-white/95 dark:bg-slate-800/95 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
        Add Step
      </div>
      <div className="grid grid-cols-2 gap-1">
        {stepTypes.map((type) => {
          const colors = getStepTypeColors(type);
          return (
            <button
              key={type}
              onClick={() => onAddStep(type)}
              className="flex items-center gap-1 px-2 py-1.5 text-xs rounded border transition-all hover:shadow-sm"
              style={{
                backgroundColor: colors.bg,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              {STEP_TYPE_LABELS[type]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============= Main Editor Component =============

function E2EFlowEditorInner(
  {
    data,
    className = '',
    onSave,
    onNodeClick,
    onEdgeClick,
  }: E2EFlowEditorProps,
  ref: React.Ref<E2EFlowEditorHandle>
) {
  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLayouting, setIsLayouting] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [editingNode, setEditingNode] = useState<Node | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Convert E2E flow data to React Flow format
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => convertE2EFlowToReactFlow(data),
    [data]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Track changes
  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  // Handle node changes
  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);
      markDirty();
    },
    [onNodesChange, markDirty]
  );

  // Handle edge changes
  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      onEdgesChange(changes);
      markDirty();
    },
    [onEdgesChange, markDirty]
  );

  // Handle new connections
  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => addEdge({
        ...connection,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
      }, eds));
      markDirty();
    },
    [setEdges, markDirty]
  );

  // Handle node click
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeClick?.(node);
    },
    [onNodeClick]
  );

  // Handle node double-click to edit
  const handleNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setEditingNode(node);
    },
    []
  );

  // Handle edge click
  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      onEdgeClick?.(edge);
    },
    [onEdgeClick]
  );

  // Handle selection change
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodesList }: { nodes: Node[] }) => {
      setSelectedNodes(selectedNodesList.map(n => n.id));
    },
    []
  );

  // Add new step
  const addStep = useCallback(
    (type: FlowStepType, _pathId?: string) => {
      const id = `new-step-${Date.now()}`;
      const viewport = reactFlowInstance.getViewport();
      const containerBounds = containerRef.current?.getBoundingClientRect();

      // Calculate center position of viewport
      const centerX = containerBounds
        ? (containerBounds.width / 2 - viewport.x) / viewport.zoom
        : 200;
      const centerY = containerBounds
        ? (containerBounds.height / 2 - viewport.y) / viewport.zoom
        : 200;

      const newNode: Node = {
        id,
        type: type,
        position: { x: centerX, y: centerY },
        data: {
          label: `New ${STEP_TYPE_LABELS[type]}`,
          type,
          description: '',
        },
      };

      setNodes((nds) => [...nds, newNode]);
      markDirty();
      setShowPalette(false);
    },
    [reactFlowInstance, setNodes, markDirty]
  );

  // Delete selected nodes
  const deleteSelected = useCallback(() => {
    if (selectedNodes.length === 0) return;

    setNodes((nds) => nds.filter((n) => !selectedNodes.includes(n.id)));
    setEdges((eds) =>
      eds.filter(
        (e) => !selectedNodes.includes(e.source) && !selectedNodes.includes(e.target)
      )
    );
    setSelectedNodes([]);
    markDirty();
  }, [selectedNodes, setNodes, setEdges, markDirty]);

  // Save step edits
  const handleStepSave = useCallback(
    (nodeId: string, stepData: Partial<E2EFlowStep>) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              type: stepData.type || node.type,
              data: {
                ...node.data,
                label: stepData.name || node.data.label,
                description: stepData.description || node.data.description,
                type: stepData.type || node.data.type,
              },
            };
          }
          return node;
        })
      );
      markDirty();
    },
    [setNodes, markDirty]
  );

  // Convert current state back to E2EFlowData
  const getFlowData = useCallback((): E2EFlowData => {
    // Group nodes by path
    const pathNodes = new Map<string, Node[]>();
    nodes.forEach((node) => {
      if (node.id.startsWith('path-header-')) return;
      const pathId = node.id.split('-step-')[0] || 'default';
      if (!pathNodes.has(pathId)) {
        pathNodes.set(pathId, []);
      }
      pathNodes.get(pathId)!.push(node);
    });

    const paths: E2EFlowPath[] = [];
    pathNodes.forEach((pathNodeList, pathId) => {
      // Sort nodes by Y position to determine order
      const sortedNodes = [...pathNodeList].sort((a, b) => a.position.y - b.position.y);

      const steps: E2EFlowStep[] = sortedNodes.map((node) => ({
        id: node.id,
        type: (node.data.type as FlowStepType) || 'service-call',
        name: (node.data.label as string) || 'Unnamed Step',
        label: (node.data.label as string) || 'Unnamed Step',
        description: node.data.description as string | undefined,
        data: node.data as Record<string, unknown>,
        sourceFile: node.data.sourceFile as string | undefined,
        sourceLine: node.data.sourceLine as number | undefined,
        evidence: [],
        isAsync: node.data.isAsync as boolean | undefined,
        duration: node.data.duration as number | undefined,
        error: node.data.error as { type: string; message: string } | undefined,
      }));

      paths.push({
        id: pathId,
        name: pathId === 'default' ? 'Main Flow' : pathId,
        triggerType: 'click',
        triggerName: steps[0]?.name || 'Unknown',
        steps,
        complete: true,
        touchedServices: [],
        touchedTables: [],
      });
    });

    return {
      paths,
      metadata: data.metadata,
    };
  }, [nodes, data.metadata]);

  // Save changes
  const save = useCallback(() => {
    const flowData = getFlowData();
    onSave?.(flowData);
    setHasUnsavedChanges(false);
  }, [getFlowData, onSave]);

  // Apply dagre auto layout
  const applyAutoLayout = useCallback(() => {
    setIsLayouting(true);
    try {
      const g = new dagre.graphlib.Graph();
      g.setGraph({
        rankdir: 'TB',
        nodesep: 60,
        ranksep: 80,
        marginx: 50,
        marginy: 50,
      });
      g.setDefaultEdgeLabel(() => ({}));

      nodes.forEach((node) => {
        g.setNode(node.id, { width: 200, height: 80 });
      });

      edges.forEach((edge) => {
        g.setEdge(edge.source, edge.target);
      });

      dagre.layout(g);

      const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = g.node(node.id);
        if (nodeWithPosition) {
          return {
            ...node,
            position: {
              x: nodeWithPosition.x - 100,
              y: nodeWithPosition.y - 40,
            },
          };
        }
        return node;
      });

      setNodes(layoutedNodes);
      markDirty();

      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
      }, 50);
    } catch (error) {
      console.error('Failed to apply auto layout:', error);
    } finally {
      setIsLayouting(false);
    }
  }, [nodes, edges, setNodes, reactFlowInstance, markDirty]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    fitView: () => reactFlowInstance.fitView({ padding: 0.2 }),
    zoomIn: () => reactFlowInstance.zoomIn(),
    zoomOut: () => reactFlowInstance.zoomOut(),
    applyAutoLayout,
    addStep,
    deleteSelected,
    save,
    getFlowData,
  }));

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodes.length > 0) {
          deleteSelected();
          e.preventDefault();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        save();
        e.preventDefault();
      }
    },
    [selectedNodes, deleteSelected, save]
  );

  return (
    <div
      ref={containerRef}
      className={`h-full w-full ${className}`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        onSelectionChange={handleSelectionChange}
        nodeTypes={e2eFlowNodeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        nodesDraggable
        nodesConnectable
        elementsSelectable
        className="bg-slate-50 dark:bg-slate-900"
      >
        <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
        <Controls />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md"
        />

        {/* Status Panel */}
        <Panel position="top-left" className="bg-white/90 dark:bg-slate-800/90 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
            <div className="font-medium flex items-center gap-2">
              E2E Flow Editor
              {hasUnsavedChanges && (
                <span className="text-orange-500">*Unsaved</span>
              )}
            </div>
            <div className="text-slate-500">
              {nodes.length} nodes, {edges.length} edges
              {selectedNodes.length > 0 && (
                <span className="text-blue-500 ml-1">
                  ({selectedNodes.length} selected)
                </span>
              )}
            </div>
          </div>
        </Panel>

        {/* Toolbar Panel */}
        <Panel position="top-right" className="flex gap-2">
          <button
            onClick={() => setShowPalette(!showPalette)}
            className={`px-3 py-1.5 text-sm border rounded-md shadow-sm ${
              showPalette
                ? 'bg-blue-500 text-white border-blue-600'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            + Add Step
          </button>
          {selectedNodes.length > 0 && (
            <button
              onClick={deleteSelected}
              className="px-3 py-1.5 text-sm bg-red-500 text-white border border-red-600 rounded-md hover:bg-red-600 shadow-sm"
            >
              Delete ({selectedNodes.length})
            </button>
          )}
          <button
            onClick={applyAutoLayout}
            disabled={isLayouting}
            className="px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm disabled:opacity-50"
          >
            {isLayouting ? 'Layouting...' : 'Auto Layout'}
          </button>
          <button
            onClick={save}
            disabled={!hasUnsavedChanges}
            className={`px-3 py-1.5 text-sm rounded-md shadow-sm ${
              hasUnsavedChanges
                ? 'bg-green-500 text-white border border-green-600 hover:bg-green-600'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-400 border border-slate-300 dark:border-slate-600 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </Panel>

        {/* Step Type Palette */}
        {showPalette && (
          <Panel position="bottom-right">
            <StepTypePalette onAddStep={addStep} />
          </Panel>
        )}

        {/* Help Panel */}
        <Panel position="bottom-left" className="bg-white/90 dark:bg-slate-800/90 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-slate-700 text-xs text-slate-500">
          <div className="space-y-0.5">
            <div>Double-click node to edit</div>
            <div>Delete/Backspace to remove selected</div>
            <div>Ctrl+S to save</div>
            <div>Drag handles to connect nodes</div>
          </div>
        </Panel>
      </ReactFlow>

      {/* Step Editor Modal */}
      {editingNode && (
        <StepEditorModal
          node={editingNode}
          onClose={() => setEditingNode(null)}
          onSave={handleStepSave}
        />
      )}
    </div>
  );
}

// Wrap with forwardRef
const E2EFlowEditorWithRef = forwardRef(E2EFlowEditorInner);

/**
 * E2E Flow Editor with ReactFlowProvider
 */
export const E2EFlowEditor = forwardRef<E2EFlowEditorHandle, E2EFlowEditorProps>(
  (props, ref) => {
    return (
      <ReactFlowProvider>
        <E2EFlowEditorWithRef {...props} ref={ref} />
      </ReactFlowProvider>
    );
  }
);

E2EFlowEditor.displayName = 'E2EFlowEditor';

export default E2EFlowEditor;
